import { RefObject, useState, useEffect, useMemo } from "react";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { Grid, Switch, Divider } from "@mui/material";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {
  ProsodyValues,
  defaultProsodyValues,
} from "shared/models/tts-editor/prosody";
import { ReactComponent as ProsodyIcon } from "assets/icons/prosody-icon.svg";
import SSMLPreview from "features/TTS/TTSCustomEditor/components/SSMLPreview";
import { SSMLTag, SSMLTagsColors } from "shared/constants";
import RateMenu from "./RateMenu";
import DurationMenu from "./DurationMenu";
import PitchMenu from "./PitchMenu";
import ContourMenu from "./ContourMenu";
import VolumeMenu from "./VolumeMenu";
import {
  isEditorEmpty,
  processSpaces,
  useEditorSelection,
} from "shared/utils/tts-editor-helper";
import { getCurrentSSMLValue } from "features/TTS/TTSCustomEditor/utils/editorKeyDownHandler";
import { useSetAtom } from "jotai";
import { contourValuesAtom } from "atoms";
import { defaultContourValues } from "shared/models";
import { useSSMLMenuDisabled } from "shared/utils/menu-disabling-rules";
import { updateExistingProsodyTag } from "shared/utils/tts-custom-editor";
import CustomIconTextButtonMenu from "../../components/CustomIconTextButtonMenu";

interface ProsodyMenuProps {
  editorRef: RefObject<EditorRef | null>;
  disabled?: boolean;
}

export default function ProsodyMenu({
  editorRef,
  disabled,
}: ProsodyMenuProps): JSX.Element {
  const editorEmpty = isEditorEmpty(editorRef);
  const hasSelection = useEditorSelection(editorRef);
  const [values, setValues] = useState<ProsodyValues>({
    ...defaultProsodyValues,
  });
  const [pendingValues, setPendingValues] = useState<ProsodyValues>({
    ...defaultProsodyValues,
  });
  const [initialValues, setInitialValues] = useState<ProsodyValues>({
    ...defaultProsodyValues,
  });

  const isSelectionDisabled = useSSMLMenuDisabled(editorRef, SSMLTag.Prosody);

  //use local state for active detection to support nested tags
  const [isActive, setIsActive] = useState(false);

  const setContourValues = useSetAtom(contourValuesAtom);

  const isDisabled =
    disabled ||
    editorEmpty ||
    isSelectionDisabled ||
    (!hasSelection && !isActive);

  // Load existing values when dialog opens or when active tags change
  useEffect(() => {
    const editor = editorRef.current?.editor;
    if (!editor) return;

    // new detection function that checks all prosody attributes deeply
    const checkActive = () => {
      const vol = getCurrentSSMLValue(editorRef, SSMLTag.Volume);
      const rate = getCurrentSSMLValue(editorRef, SSMLTag.Rate);
      const dur = getCurrentSSMLValue(editorRef, SSMLTag.Duration);
      const pitch = getCurrentSSMLValue(editorRef, SSMLTag.Pitch);
      const contour = getCurrentSSMLValue(editorRef, SSMLTag.Contour);

      const hasProsody = !!(vol || rate || dur || pitch || contour);
      setIsActive(hasProsody);

      if (hasProsody) {
        const newValues = { ...defaultProsodyValues };

        // Check for all prosody attributes in the data-ssml string
        if (vol) newValues.volume = vol;
        if (rate) newValues.rate = rate;
        if (dur) newValues.duration = dur;
        if (pitch) newValues.pitch = pitch;
        if (contour) {
           newValues.contour = contour;
        } else {
           newValues.contour = "";
        }
        
        setValues(newValues);
        setPendingValues(newValues);
        setInitialValues(newValues);
      }
    };

    editor.on("NodeChange KeyUp", checkActive);
    checkActive();

    return () => {
      editor.off("NodeChange KeyUp", checkActive);
    };
  }, [editorRef, setContourValues]);

  const handleChange = (attr: keyof ProsodyValues, checked: boolean) => {
    setValues((prev) => {
      const newValues = { ...prev };

      if (attr === "rate" && checked) {
        newValues.duration = "";
      }
      if (attr === "duration" && checked) {
        newValues.rate = "";
      }
      if (attr === "pitch" && checked) {
        newValues.contour = "";
      }
      if (attr === "contour" && checked) {
        newValues.pitch = "";
      }

      if (checked) {
        switch (attr) {
          case "duration":
            newValues[attr] = "100ms";
            break;
          case "contour":
            newValues[attr] = "(0%,0st) (100%,0st)";
            break;
          case "volume":
            newValues[attr] = "silent";
            break;
          default:
            newValues[attr] = "default";
        }
      } else {
        newValues[attr] = "";
      }
      return newValues;
    });

    // Reset corresponding pending value when toggling
    setPendingValues((prev) => ({
      ...prev,
      // Reset rate/duration
      ...(attr === "rate" && checked ? { duration: "" } : {}),
      ...(attr === "duration" && checked ? { rate: "" } : {}),

      // Reset pitch/contour
      ...(attr === "pitch" && checked ? { contour: "" } : {}),
      ...(attr === "contour" && checked ? { pitch: "" } : {}),

      [attr]: checked
        ? attr === "duration"
          ? "100ms"
          : attr === "volume"
            ? "silent"
            : attr === "contour"
              ? "(0%,0st) (100%,0st)"
              : "default"
        : "",
    }));
  };

  const updatePendingValue = (type: keyof ProsodyValues, value: string) => {
    setPendingValues((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  // Calculate if values have changed from initial values
  const hasChanges = useMemo(() => {
    return (
      pendingValues.volume !== initialValues.volume ||
      pendingValues.rate !== initialValues.rate ||
      pendingValues.duration !== initialValues.duration ||
      pendingValues.pitch !== initialValues.pitch ||
      pendingValues.contour !== initialValues.contour
    );
  }, [pendingValues, initialValues]);

  const shouldEnableUpdateButton = useMemo(() => {
    if (!isActive) {
      return (
        pendingValues.volume !== "" ||
        pendingValues.rate !== "" ||
        pendingValues.duration !== "" ||
        pendingValues.pitch !== "" ||
        pendingValues.contour !== ""
      );
    }
    // For existing tags, only enable if values have changed
    return hasChanges;
  }, [isActive, pendingValues, hasChanges]);

  const handleDialogApply = () => {
    const editor = editorRef.current?.editor;
    if (!editor) return;

    const selectedHtml = editor.selection.getContent({ format: "html" });
    const uid = Date.now().toString();

    const activeAttributes: string[] = [];

    if (pendingValues.volume) {
      activeAttributes.push(`volume-${uid}=${pendingValues.volume}`);
    }
    if (pendingValues.rate) {
      activeAttributes.push(`rate-${uid}=${pendingValues.rate}`);
    }
    if (pendingValues.duration) {
      activeAttributes.push(`duration-${uid}=${pendingValues.duration}`);
    }
    if (pendingValues.pitch) {
      activeAttributes.push(`pitch-${uid}=${pendingValues.pitch}`);
    }
    if (pendingValues.contour) {
      activeAttributes.push(`contour-${uid}=${pendingValues.contour}`);
    }

    // Update existing prosody tag
    let updated = updateExistingProsodyTag(editor, activeAttributes);

    if (!updated) {
       const prosodySelector = [
        `[data-ssml*="${SSMLTag.Volume}-"]`,
        `[data-ssml*="${SSMLTag.Rate}-"]`,
        `[data-ssml*="${SSMLTag.Duration}-"]`,
        `[data-ssml*="${SSMLTag.Pitch}-"]`,
        `[data-ssml*="${SSMLTag.Contour}-"]`
       ].join(",");
       
       const existingNode = editor.selection.getNode().closest(prosodySelector) as HTMLElement;
       
       if (existingNode) {
          if (activeAttributes.length > 0) {
             existingNode.setAttribute("data-ssml", activeAttributes.join(","));
          } else {
             editor.dom.remove(existingNode, true);
          }
          updated = true;
          // Fix: Defer change event to allow dialog to close first
          setTimeout(() => editor.fire("change"), 0);
       }
    }

    if (!updated && activeAttributes.length > 0) {
      // Create data-ssml attribute combining all active attributes
      const dataSSMLAttributes = activeAttributes.join(",");

      const wrapProsodySpan = (html: string) => {
        const {
          content: actualContent,
          startsWithSpace,
          endsWithSpace,
        } = processSpaces(html);

        // Add leading space if it was in the original selection
        const leadingSpace = startsWithSpace ? " " : "";
        // Add trailing space if it was in the original selection
        const trailingSpace = endsWithSpace ? " " : "";

        // Create the span with combined attributes
        return `${leadingSpace}<span style='text-decoration: underline; text-decoration-color: ${SSMLTagsColors.ProsodyTag};' data-ssml='${dataSSMLAttributes}'>${actualContent}</span>${trailingSpace}`;
      };

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = selectedHtml;

      const blockSelector = "p,div,h1,h2,h3,h4,h5,h6,li,blockquote,td,th";
      const blocks = Array.from(tempDiv.querySelectorAll(blockSelector));
      const hasBlocks = blocks.length > 0;

      if (hasBlocks) {
        blocks.forEach((block) => {
          const inner = block.innerHTML;
          if (!inner || !inner.replace(/&nbsp;|\s+/g, "").length) return;
          block.innerHTML = wrapProsodySpan(inner);
        });

        editor.selection.setContent(tempDiv.innerHTML);
      } else {
        editor.selection.setContent(wrapProsodySpan(selectedHtml));
      }
      setTimeout(() => editor.fire("change"), 0);
    } else if (updated) {
      setTimeout(() => editor.fire("change"), 0);
    }
    
    setInitialValues({ ...pendingValues });
  };

  const ProsodySwitch = ({
    label,
    attr,
  }: {
    label: string;
    attr: keyof ProsodyValues;
  }) => (
    <Grid container alignItems="center" spacing={2}>
      <Grid item>
        <Switch
          id={`${attr}-switch`}
          checked={values[attr] !== ""}
          onChange={(e) => handleChange(attr, e.target.checked)}
        />
      </Grid>
      <Grid item>
        <Typography variant="subtitle1">{label}</Typography>
      </Grid>
    </Grid>
  );

  // Reset dialog state when closing
  const handleDialogClose = () => {
    if (!isActive) {
      // Only reset if there's no active tag
      setPendingValues({ ...defaultProsodyValues });
      setValues({ ...defaultProsodyValues });
      setContourValues(structuredClone(defaultContourValues));
    }
  };

  return (
    <CustomIconTextButtonMenu
      useDialog
      componentId="prosody"
      menuTitle="Prosody"
      dialogHeader="Prosody"
      onDialogPrimaryButtonClick={handleDialogApply}
      onDialogClose={handleDialogClose}
      onDialogOpen={handleDialogClose}
      isActive={isActive}
      editorRef={editorRef}
      showClearButton={isActive}
      icon={ProsodyIcon}
      tagType={SSMLTag.Prosody}
      isEditorEmpty={editorEmpty}
      shouldBeDisabled={isDisabled}
      footerText={<SSMLPreview type="prosody" prosodyValues={pendingValues} />}
      isDialogPrimaryButtonDisabled={!shouldEnableUpdateButton}
      isSelectionDisabled={isSelectionDisabled}
    >
      <Box sx={{ pt: 2 }}>
        <Box sx={{ display: "flex" }}>
          <Grid container alignItems="center">
            <Box sx={{ width: "48%" }}>
              <ProsodySwitch label="Volume" attr="volume" />
            </Box>
            {values.volume && (
              <Box sx={{ width: "48%" }}>
                <VolumeMenu
                  editorRef={editorRef}
                  onVolumeChange={(value) =>
                    updatePendingValue("volume", value)
                  }
                />
              </Box>
            )}
          </Grid>
        </Box>
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ width: "48%" }}>
            <ProsodySwitch label="Rate" attr="rate" />
            {values.rate && (
              <RateMenu
                editorRef={editorRef}
                onRateChange={(value) => updatePendingValue("rate", value)}
              />
            )}
          </Box>
          <Box sx={{ width: "48%" }}>
            <ProsodySwitch label="Duration" attr="duration" />
            {values.duration && (
              <DurationMenu
                editorRef={editorRef}
                onDurationChange={(value) =>
                  updatePendingValue("duration", value)
                }
              />
            )}
          </Box>
        </Box>
        <Divider sx={{ my: 3 }} />
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ width: "48%" }}>
            <ProsodySwitch label="Pitch" attr="pitch" />
            {values.pitch && (
              <PitchMenu
                editorRef={editorRef}
                onPitchChange={(value) => updatePendingValue("pitch", value)}
              />
            )}
          </Box>

          <Box sx={{ width: "48%" }}>
            <ProsodySwitch label="Contour" attr="contour" />
          </Box>

          {values.contour && (
            <Box sx={{ width: "100%" }}>
              <ContourMenu
                editorRef={editorRef}
                onContourChange={(value) =>
                  updatePendingValue("contour", value)
                }
              />
            </Box>
          )}
        </Box>
      </Box>
    </CustomIconTextButtonMenu>
  );
}